import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import { financePayableItinerarySummaryQueryKey, financePaymentRequestQueryKey } from "../config";
import {
  fetchFinancePaymentRequestFormContext,
  resubmitFinancePaymentRequest,
  submitFinancePaymentRequest,
  uploadFinancePaymentRequestAttachment,
} from "../repo/financePaymentRequestRepo";
import {
  defaultFinancePaymentRequestDepartmentId,
  emptyFinancePaymentRequestDraft,
  financePaymentRequestAmountAfterTicking,
  financePaymentRequestLineTotal,
  financePaymentRequestTickableLineIds,
  resolveFinancePaymentRequestPayee,
  validateFinancePaymentRequestAttachment,
  validateFinancePaymentRequestDraft,
} from "../service/financePaymentRequestService";
import type {
  FinancePaymentRequestDraft,
  FinancePaymentRequestResubmit,
  FinancePaymentRequestSubmitResult,
  FinancePaymentRequestTarget,
} from "../types";

/**
 * 请款表单：读表单要用的数和选项、勾确认件行、上传附件、提交。
 * 部门没手动选时用默认部门（主部门或第一个）；收款账户没手动切过时，有已存账户就选已存的并预选默认收款账户。
 * 改勾选时金额没手改过就跟着勾选合计走。提交成功后定向刷新应付清单和请款记录。上传、提交时防重复点击。
 * 预付款（prepayment）：结算主体在表单里选，没选之前 target 为空、不读数；款项类型固定为预付款，不勾确认件行，
 * 备注必填，这个团里这个结算主体还没有确认协议时不受可请额度限制。
 * 修改后重新提交（resubmit）：表单回填原值，收款账户默认沿用原来的；回填的部门不在我现在的部门里时用默认部门，
 * 回填的行现在勾不了的不带；提交走重新提交，开新一轮审批。
 */
export function useFinancePaymentRequestForm(
  target: FinancePaymentRequestTarget | null,
  prepayment = false,
  resubmit: FinancePaymentRequestResubmit | null = null,
) {
  const client = useQueryClient();
  const context = useQuery({
    queryKey: [...financePaymentRequestQueryKey, "form-context", target?.itineraryId, target?.settlementSupplierId],
    queryFn: () =>
      fetchFinancePaymentRequestFormContext(
        (target as FinancePaymentRequestTarget).itineraryId,
        (target as FinancePaymentRequestTarget).settlementSupplierId,
      ),
    enabled: target !== null,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [fields, setFields] = useState<FinancePaymentRequestDraft>(
    () => resubmit?.initial ?? emptyFinancePaymentRequestDraft(null),
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState("");
  const [serverError, setServerError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<FinancePaymentRequestSubmitResult | null>(null);
  const submitLock = useRef(false);
  const departments = context.data?.departments ?? [];
  const payeeAccounts = context.data?.payeeAccounts ?? [];
  const lines = prepayment ? [] : (context.data?.lines ?? []);
  const draft: FinancePaymentRequestDraft = {
    ...fields,
    paymentType: prepayment ? "prepayment" : fields.paymentType,
    departmentId: departments.some((item) => item.deptId === fields.departmentId)
      ? fields.departmentId
      : defaultFinancePaymentRequestDepartmentId(departments),
    lineIds: financePaymentRequestTickableLineIds(fields.lineIds, lines),
    ...resolveFinancePaymentRequestPayee(fields, payeeAccounts),
  };

  return {
    context,
    departments,
    payeeAccounts,
    lines,
    draft,
    /** 修改后重新提交时原收款账户的显示；发起请款时为空。 */
    originalPayee: resubmit?.originalPayee ?? null,
    /** 勾的行的合计；一行没勾为空。 */
    lineTotal: financePaymentRequestLineTotal(lines, draft.lineIds),
    /** 改勾选（按行号排好）；金额没填或还等于原来的勾选合计时跟着变成新的合计。 */
    setLineIds(lineIds: string[]) {
      const ordered = financePaymentRequestTickableLineIds(lineIds, lines);
      setFields((current) => ({
        ...current,
        lineIds: ordered,
        amount: financePaymentRequestAmountAfterTicking(current.amount, lines, current.lineIds, ordered),
      }));
      setErrors([]);
      setServerError("");
    },
    /** 改一个字段；改了就收起上一次的提示。款项类型改成预付款时清掉已勾的行（预付款不勾确认件行），金额不动。 */
    setField<K extends keyof FinancePaymentRequestDraft>(key: K, value: FinancePaymentRequestDraft[K]) {
      setFields((current) => ({
        ...current,
        [key]: value,
        ...(key === "paymentType" && value === "prepayment" ? { lineIds: [] } : {}),
      }));
      setErrors([]);
      setServerError("");
    },
    errors,
    uploadError,
    serverError,
    uploading,
    submitting,
    result,
    /** 先在前端校验文件，再上传；上传成功的附件加进列表。 */
    async upload(file: File) {
      const error = validateFinancePaymentRequestAttachment(file);
      setUploadError(error);
      if (error || uploading) return;
      setUploading(true);
      try {
        const attachment = await uploadFinancePaymentRequestAttachment(file);
        setFields((current) => ({ ...current, attachments: [...current.attachments, attachment] }));
        setErrors([]);
      } catch (uploadFailure) {
        setUploadError(resolveReadableHttpErrorMessage(uploadFailure, "附件上传失败，请重试。"));
      } finally {
        setUploading(false);
      }
    },
    /** 从这次请款里拿掉一个附件（文件留在服务器上，不再绑定）。 */
    removeAttachment(attachmentId: string) {
      setFields((current) => ({
        ...current,
        attachments: current.attachments.filter((item) => item.attachmentId !== attachmentId),
      }));
    },
    /** 校验通过才提交；后端拦下时原样显示原因，表单留着可改。 */
    async submit() {
      if (!target || !context.data || submitLock.current || uploading) return;
      const found = validateFinancePaymentRequestDraft(
        draft,
        context.data.availableAmount,
        departments.length > 0,
        context.data.hasAgreement,
      );
      setErrors(found);
      setServerError("");
      if (found.length > 0) return;
      submitLock.current = true;
      setSubmitting(true);
      try {
        setResult(
          resubmit
            ? await resubmitFinancePaymentRequest(resubmit.requestId, target, draft)
            : await submitFinancePaymentRequest(target, draft),
        );
        void client.invalidateQueries({ queryKey: financePayableItinerarySummaryQueryKey(target.itineraryId) });
        void client.invalidateQueries({ queryKey: financePaymentRequestQueryKey });
      } catch (submitFailure) {
        setServerError(resolveReadableHttpErrorMessage(submitFailure, "提交失败，请稍后重试。"));
      } finally {
        submitLock.current = false;
        setSubmitting(false);
      }
    },
  };
}
